/**
 * shared/billing.ts — Pear Plus entitlement model (card 32BEnNO6I2kx).
 *
 * Decision (Jonathan, 2026-10-10, email msg_81502d3ba5ec4e0a9075afb0c191837f):
 *   Pear Plus   $9.99/mo or $79.99/yr — gifting concierge, date concierge,
 *               unlimited concierge invocation, weekly digest ("briefing"),
 *               email inbox, partner interest digest.
 *   Pear Essentials  free — everything not listed above.
 *
 * One subscription covers the couple (both members), which is the whole
 * pricing pitch — so the entitlement lives on the couple's Durable Object,
 * never per member. `plan` is the truth; `plus` is derived and kept trivial.
 *
 * "Unlimited concierge invocation" is the one surface whose FREE allowance is
 * still an open decision (MTDMpjkQKZup: 30 turns/mo recommended, not chosen).
 * The meter below ships wired but UNCAPPED until that number lands: allowance
 * null = no cap enforced. Plus is always unlimited.
 */

export type Plan = 'essentials' | 'plus';
export type BillingInterval = 'month' | 'year';
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid' | 'paused' | null;

/** The Plus surfaces, named as Jonathan named them. Used by the gates,
 *  the upgrade sheet and the probe — one list, so all three agree.
 *
 *  The first six are the original paywall (card 32BEnNO6I2kx). The last three
 *  land the standing rule Jonathan set on 2026-10-19 — "any feature with a
 *  meaningful variable inference cost sits behind Plus" — on the surfaces the
 *  delineation note (XEq1ZdeC1njF, card vPoiTtAn26lI) marked "to gate":
 *    games                 generated rounds, decoys, fuzzy answer matching
 *    notebook_filing       one model call per saved item
 *    voice_transcription   Groq STT per minute (both routes: /api/transcribe
 *                          and Plan's audio path)
 *  Essentials keeps the manual routes that make each gate honest — the
 *  notchier picker, its own typed cards, the deterministic link cascade. */
export const PLUS_SURFACES = [
  'gift_concierge',
  'date_concierge',
  'concierge_turns',
  'weekly_briefing',
  'email_inbox',
  'interest_digest',
  'games',
  'notebook_filing',
  'voice_transcription',
] as const;
export type PlusSurface = (typeof PLUS_SURFACES)[number];

/** Human labels for the upgrade sheet — same order as PLUS_SURFACES. */
export const PLUS_SURFACE_LABELS: Readonly<Record<PlusSurface, string>> = {
  gift_concierge: 'Gifting concierge',
  date_concierge: 'Date concierge',
  concierge_turns: 'Unlimited concierge',
  weekly_briefing: 'Weekly briefing',
  email_inbox: 'Email inbox',
  interest_digest: 'Partner interest digest',
  games: 'Games with the concierge',
  notebook_filing: 'Notebook smart filing',
  voice_transcription: 'Voice & meeting transcription',
};

export interface Entitlement {
  plan: Plan;
  plus: boolean;
  customerId: string | null;
  subscriptionId: string | null;
  priceId: string | null;
  interval: BillingInterval | null;
  status: SubscriptionStatus;
  currentPeriodEnd: number | null;
  plusSince: number | null;
  /** Trial end (ms since epoch). Set once, at couple creation. */
  trialEnd: number | null;
  /** True while the trial is what is granting access (no subscription yet). */
  trialing: boolean;
  /** Promo grant on the couple (card yWsCXHAXQ3Ej): the code redeemed, and
   *  its end (null = indefinite — the plus2199 case). No grant = both null. */
  promoCode: string | null;
  promoEnd: number | null;
  /** Referral grant (card 3MWZ8Zs7jA7e): the end of the days earned from
   *  referring a couple (referrer side) or from being referred (friend side).
   *  Null = never earned any. Additive and time-based like the promo grant. */
  referralEnd: number | null;
  /** Days earned while the couple was PAYING and still unconsumed (card
   *  3MWZ8Zs7jA7e). They are spent — into referralEnd — the moment a
   *  subscription stops covering the couple, so a renewal can never swallow
   *  the reward. 0 = nothing banked. */
  referralHoldDays: number;
  /** Concierge turns used this calendar month (couple-wide). */
  conciergeUsed: number;
  /** null = unlimited (Plus, or free allowance not yet decided). */
  conciergeAllowance: number | null;
}

/** Stripe statuses that keep Plus live. `past_due` keeps access through the
 *  dunning window — a card that needs a retry must not lock a couple out of
 *  their own product mid-week; `canceled` ends access at period end, which
 *  the webhook models by flipping the plan. */
export const ENTITLED_STATUSES: readonly Exclude<SubscriptionStatus, null>[] = ['active', 'trialing', 'past_due'];

export function isEntitled(status: SubscriptionStatus): boolean {
  return status !== null && (ENTITLED_STATUSES as readonly string[]).includes(status);
}

/** Free trial length in days. Same shape as PEAR_CONCIERGE_FREE_TURNS: unset
 *  or unparsable -> the default, so prod ships a trial without a secret. */
export function trialDays(env: { PEAR_TRIAL_DAYS?: string }, fallback = 30): number {
  const raw = env.PEAR_TRIAL_DAYS;
  if (raw === undefined || raw === null || String(raw).trim() === '') return fallback;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.floor(n);
}

/** The trial grants access ONLY to a couple that has never subscribed. A real
 *  subscription is the whole truth from the moment it exists: a canceled sub
 *  must not fall back to reviving the acquisition trial, or a couple that
 *  chose to leave keeps the Plus surfaces they just walked away from. */
export function trialActive(trialEnd: number | null | undefined, subscriptionId: string | null | undefined, now: number): boolean {
  if (subscriptionId) return false;
  return typeof trialEnd === 'number' && trialEnd > now;
}

/** A promo grant keeps Plus regardless of the clock and regardless of any
 *  subscription the couple may later have — it was granted, not bought, so a
 *  canceled sub must not revoke it (same reasoning as the trial, opposite
 *  direction). `durationDays: null` = indefinite (the plus2199 case). */
export function promoActive(promoCode: string | null | undefined, promoEnd: number | null | undefined, now: number): boolean {
  if (!promoCode) return false;
  // Revocation must revoke on the READ path, not only on redemption: the
  // founder flips PROMO_CODES[code].revoked in a deploy, and every couple
  // already holding that grant has to drop to Essentials on its next
  // entitlement derivation. A code that is no longer listed at all reads as
  // revoked too — the table is the source of truth, so removing an entry is
  // the same kill as flagging it. (sol-review IsfArxwGo4FX.)
  const entry = PROMO_CODES[promoCode];
  if (!entry || entry.revoked) return false;
  return promoEnd === null || (typeof promoEnd === 'number' && promoEnd > now);
}

/* ---- Referrals (card 3MWZ8Zs7jA7e) ----
 * Ported from JitsLab (jonathan 2026-10-17: "referal should work the same as
 * jitslab for users eg get 30 addtl days"). Both sides earn the same days: the
 * friend's grant lands when their account redeems the code, the referrer's
 * when the friend's couple actually FORMS. JitsLab vests on the referred
 * user's first logged session; Pear's equivalent activation event is the
 * second partner joining — the same moment the trial clock starts. Deferring
 * the referrer's days is the anti-gaming rule: an empty signup pays nothing. */

/** Days of Plus each side earns per successful referral. */
export const REFERRAL_REWARD_DAYS = 30;

/* ---- The founder code (card NThh9EjYzupt) ----
 * Jonathan's own referral code (email 2026-10-21: "mine IE Jon's should refer
 * user with unlimited plus ie expires 31 December 2099"). It is a REFERRAL
 * code, not a promo: it travels as a share link and a QR exactly like a
 * couple's own code, and it is redeemed through the same apply path. What
 * differs is what the friend is paid — Plus until FOUNDER_PLUS_END, not 30
 * days — so it takes an ABSOLUTE end.
 *
 * Why an explicit end and not the plus2199 indefinite grant: a portfolio rule
 * says an unset expiry must never read as unlimited, so the founder grant
 * carries a real number. 2099-12-31 is that number, and it is the recorded
 * expiry the card asks for; nothing in the product treats it specially.
 *
 * Why this exact string: minted codes are six characters from [a-z0-9], so a
 * longer code can never collide with one. It is deliberately NOT guessable —
 * anyone who typed it would be handed permanent Plus — so it is not a word,
 * and it is the only place it is written. */
export const FOUNDER_REFERRAL_CODE = 'pearfounderjf';

/** The recorded expiry of a founder grant: 2099-12-31T00:00:00Z. */
export const FOUNDER_PLUS_END = Date.UTC(2099, 11, 31);

/** Accounts whose own share card hands out the founder link instead of the
 *  couple's 30-day one. Same shape as PROMO_CODES' allowedEmails — one list,
 *  editable only by a deploy. */
export const FOUNDER_EMAILS: readonly string[] = ['jonathan.e.farmer@gmail.com'];

/** True when `raw` is the founder code (typed by a human, so trimmed and
 *  lowercased like every other code). */
export function isFounderReferralCode(raw: unknown): boolean {
  return typeof raw === 'string' && raw.trim().toLowerCase() === FOUNDER_REFERRAL_CODE;
}


/** How long a `?ref=` capture survives on the device (JitsLab uses 60 days). */
export const REFERRAL_COOKIE_DAYS = 60;

/** A referral grant keeps Plus until its own end, regardless of the clock and
 *  regardless of a subscription — granted, not bought (same reasoning as the
 *  promo grant, and unlike the trial it does not end at the first
 *  subscription: a couple who referred a friend keeps those days). */
export function referralActive(referralEnd: number | null | undefined, now: number): boolean {
  return typeof referralEnd === 'number' && referralEnd > now;
}

/* ---- Promo codes (card yWsCXHAXQ3Ej) ----
 * Founder-managed, shipped in code like PLUS_PRICES: the list is small,
 * changing it is a deploy (RED-gated, Jonathan's call), and a code must never
 * be grantable by anything a user can write. `durationDays: null` grants
 * Plus indefinitely; `allowedEmails` (non-empty) restricts redemption to the
 * named accounts' couple — everything about the grant is fail-closed. */
export interface PromoCode {
  durationDays: number | null;
  allowedEmails: readonly string[];
  revoked: boolean;
}

export const PROMO_CODES: Readonly<Record<string, PromoCode>> = {
  plus2199: {
    durationDays: null,
    // Both founder addresses (card felJxdYc8NWH): the check intersects this
    // list with every account email linked to the couple, so a couple either
    // partner belongs to can redeem — Rasha gets the indefinite grant too.
    allowedEmails: ['jonathan.e.farmer@gmail.com', 'rasha.rar@gmail.com'],
    revoked: false,
  },
};

/** Resolve a submitted code to its entry. Case-insensitive (codes are typed
 *  by humans), trimmed; unknown or revoked → null, and the caller refuses. */
export function promoCodeFor(raw: unknown): { code: string; promo: PromoCode } | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toLowerCase();
  const promo = PROMO_CODES[code];
  if (!promo || promo.revoked) return null;
  return { code, promo };
}

/** A subscription that is still costing someone money. `canceled` ends access
 *  at period end, so a canceled sub inside its paid window is LIVE for guard
 *  purposes — abandoning that couple for a new one orphans the billing. */
export function hasLiveSubscription(ent: Pick<Entitlement, 'subscriptionId' | 'status' | 'currentPeriodEnd'>, now = Date.now()): boolean {
  if (!ent.subscriptionId) return false;
  if (isEntitled(ent.status)) return true;
  return ent.status === 'canceled' && typeof ent.currentPeriodEnd === 'number' && ent.currentPeriodEnd > now;
}

export const PLUS_PRICE_LOOKUP_MONTHLY = 'pear_plus_monthly';
export const PLUS_PRICE_LOOKUP_YEARLY = 'pear_plus_yearly';

/** Display prices (cents, USD) — the source of truth for what Jonathan
 *  approved; Stripe carries the same numbers and the probe asserts both. */
export const PLUS_PRICES: Readonly<Record<BillingInterval, { lookupKey: string; cents: number; currency: string; label: string }>> = {
  month: { lookupKey: PLUS_PRICE_LOOKUP_MONTHLY, cents: 999, currency: 'usd', label: '$9.99/month' },
  year: { lookupKey: PLUS_PRICE_LOOKUP_YEARLY, cents: 7999, currency: 'usd', label: '$79.99/year' },
};

/** What a successful take hands back — enough to refund THAT turn later.
 *  `id` makes the refund exactly-once (card VyoILwvZV1K5: a refund used to be
 *  fire-and-forget, so a throwing refund could be attempted twice), and
 *  `month` is the month CHARGED, not the month the refund happens in — a turn
 *  taken at 23:59 on the 31st and refunded after midnight used to decrement
 *  the brand-new month instead. `ts` is when the turn was taken, so the commit
 *  step can tell whether a card actually landed before settling it. */
export interface ConciergeTurnReservation {
  id: string;
  month: string;
  ts: number;
}

/** The gate answer for the agent producer: allowed + the reservation to hand
 *  back if no card is produced. `reservation` is null when nothing was charged
 *  — enforcement off (no take happened) or the tap was denied. */
export interface ConciergeTurnGate {
  allowed: boolean;
  reservation: ConciergeTurnReservation | null;
}

export interface ConciergeTurnResult {
  allowed: boolean;
  used: number;
  allowance: number | null;
  plan: Plan;
  /** Present only when a turn was actually charged. */
  reservation?: ConciergeTurnReservation | null;
}

/** What the webhook / checkout told us about the subscription. Every field
 *  optional: a partial event (customer created before the sub exists) must
 *  update only what it knows, never null the rest. */
export interface SubscriptionUpdate {
  customerId?: string | null;
  subscriptionId?: string | null;
  priceId?: string | null;
  interval?: BillingInterval | null;
  status?: SubscriptionStatus;
  currentPeriodEnd?: number | null;
  /** Probe/dev only: move the trial end (a past timestamp expires the trial).
   *  Nothing in prod sets this — a trial ends by the clock, by design. */
  trialEnd?: number | null;
}

/**
 * Enforcement switch (PEAR_BILLING_ENFORCE), shared by the worker routes and
 * the Durable Object scans. Unset = gates OFF: development and every existing
 * probe keep running against the product as it behaves today, and the paywall
 * deploy turns it on with one variable — same shape as PEAR_INGEST_ALLOWLIST.
 */
export function billingEnforced(env: { PEAR_BILLING_ENFORCE?: string }): boolean {
  return env.PEAR_BILLING_ENFORCE === '1' || env.PEAR_BILLING_ENFORCE === 'on';
}

/* ---- E1. The entitlement record (epic wPE7ApNqtqpa, card Og4XlO6YucTx) ----
 *
 * Three payment systems converge on this one shape: Stripe (live today),
 * StoreKit and Google Play (both gated on the store accounts), plus the grant
 * paths the product already has (trial, promo, referral). E2's receipt
 * validators write a source and revoke through here instead of inventing a
 * second model.
 *
 * It is deliberately COUPLE-scoped, exactly like the row it is derived from:
 * one subscription covers the couple, and a per-member clock is a separate
 * decision Jonathan has not made (card urTPsbtlAeXb is blocked on that GO).
 *
 * Everything below is pure — the DO owns storage and the lock; this owns the
 * shape and the derivation, so the contract tests can exercise every state
 * without booting a worker. */

/** Which system wrote this record. `stripe` is the only live writer in E1;
 *  `storekit` and `play` arrive with E2/E3; `grant` covers trial, promo and
 *  referral. Null = nothing has ever written it. */
export type EntitlementSource = 'stripe' | 'storekit' | 'play' | 'grant';

/** `entitled` — Plus is live now. `expired` — a source exists but no longer
 *  grants (a lapsed trial, a canceled subscription past its period). `none`
 *  — the couple has never held any source. */
export type EntitlementState = 'entitled' | 'expired' | 'none';

export interface EntitlementRecord {
  source: EntitlementSource | null;
  state: EntitlementState;
  plus: boolean;
  plan: Plan;
  /** End of the window that is granting access. Null = indefinite grant
   *  (the plus2199 promo) or no source at all. */
  periodEnd: number | null;
  /** When a payment system last confirmed this record (ms epoch). Null =
   *  never — a grant is not a verification, so grants do not stamp it. */
  lastVerifiedAt: number | null;
}

/** E1 flag (epic wPE7ApNqtqpa). Same shape as PEAR_BILLING_ENFORCE: unset =
 *  off, so prod keeps the read path it has today and the new one is proven
 *  locally before anything flips it. */
export function entitlementsEnabled(env: { PEAR_PLUS_ENTITLEMENTS?: string }): boolean {
  return env.PEAR_PLUS_ENTITLEMENTS === '1' || env.PEAR_PLUS_ENTITLEMENTS === 'on';
}

/** The stored source is the writer stamp. Rows written before the column
 *  existed — and every grant, which stamps nothing — derive theirs from what
 *  is actually granting, so a record is never sourceless just because it is
 *  old. */
export function deriveEntitlementSource(ent: Pick<Entitlement, 'subscriptionId' | 'trialEnd' | 'promoCode' | 'referralEnd'>): EntitlementSource | null {
  if (ent.subscriptionId) return 'stripe';
  if (ent.trialEnd || ent.promoCode || ent.referralEnd) return 'grant';
  return null;
}

/** A stored value is only a source if the type says it is. Anything else —
 *  an old column, a hand-edited row, a writer from a future branch — falls
 *  back to derivation rather than leaking out as a source that exists. */
export function isEntitlementSource(value: unknown): value is EntitlementSource {
  return value === 'stripe' || value === 'storekit' || value === 'play' || value === 'grant';
}

/** Build the record. The `plus` answer is ent.plus and nothing else, so the
 *  flag-on read and the legacy expression cannot disagree — that identity is
 *  what the probe asserts. */
export function entitlementRecord(
  ent: Entitlement,
  storedSource: EntitlementSource | null,
  lastVerifiedAt: number | null | undefined,
  now = Date.now(),
): EntitlementRecord {
  const source = isEntitlementSource(storedSource) ? storedSource : deriveEntitlementSource(ent);
  const hasSource = Boolean(ent.subscriptionId || ent.trialEnd || ent.promoCode || ent.referralEnd);
  const state: EntitlementState = ent.plus ? 'entitled' : hasSource ? 'expired' : 'none';
  // The window that is granting, in authority order. An inactive grant has
  // already been nulled by toEntitlement, so each branch is live coverage.
  const periodEnd =
    typeof ent.currentPeriodEnd === 'number' && isEntitled(ent.status) && ent.subscriptionId
      ? ent.currentPeriodEnd
      : ent.trialing && typeof ent.trialEnd === 'number'
        ? ent.trialEnd
        : ent.promoCode
          ? ent.promoEnd
          : typeof ent.referralEnd === 'number'
            ? ent.referralEnd
            : typeof ent.trialEnd === 'number'
              ? ent.trialEnd
              : null;
  return {
    source,
    state,
    plus: ent.plus,
    plan: ent.plan,
    periodEnd,
    lastVerifiedAt: typeof lastVerifiedAt === 'number' ? lastVerifiedAt : null,
  };
}
